<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import Message from 'primevue/message';
import { TrackerPicker, type TrackerPickerValue } from '@gpx-spa/ui';
import { useAssignableTrackers } from '@/composables/useAssignableTrackers';

const devicesValue = defineModel<TrackerPickerValue[]>('devices', {
  default: () => [],
});

const props = withDefaults(
  defineProps<{
    deviceError?: string;
    /** When false, skips device list load until visible (e.g. wizard publish step). */
    loadDevicesWhenVisible?: boolean;
    /** @deprecated Use loadDevicesWhenVisible */
    active?: boolean;
    description?: string;
    /** Max trackers selectable; null = unlimited convoy (ENG-2580), 1 = single (ENG-2710). */
    maxSelection?: number | null;
  }>(),
  {
    deviceError: '',
    loadDevicesWhenVisible: true,
    active: true,
    description:
      'Search and select one or more trackers to assign to this shipment.',
    maxSelection: null,
  },
);

const devicesEnabled = computed(
  () => props.loadDevicesWhenVisible && props.active !== false,
);

const vuexStore = useStore();
const accountId = computed(() => vuexStore.getters['auth/accountId'] as number);

const { devices, loading } = useAssignableTrackers({
  accountId,
  active: () => devicesEnabled.value,
});

const trackerPickerRef = ref<{ focusSearch?: () => boolean } | null>(null);

function focusFirstField(): boolean {
  return trackerPickerRef.value?.focusSearch?.() ?? false;
}

defineExpose({
  focusFirstField,
});
</script>

<template>
  <section
    data-testid="shipment-tracker-picker-section"
    class="space-y-4 rounded-lg border border-gray-200 bg-white p-4"
    :class="{ 'ring-1 ring-red-200': Boolean(deviceError) }"
  >
    <p v-if="description" class="text-sm text-gpx_gray-200">
      {{ description }}
    </p>

    <TrackerPicker
      ref="trackerPickerRef"
      v-model="devicesValue"
      :devices="devices"
      :loading="loading"
      :active="devicesEnabled"
      :max-selection="maxSelection"
    />

    <Message
      v-if="deviceError"
      severity="error"
      size="small"
      variant="simple"
      class="mt-1"
      :closable="false"
    >
      {{ deviceError }}
    </Message>
  </section>
</template>
